/**
 * Authentication utilities
 * Based on ENCRYPTION_KEYS_USAGE.md
 */

import { SignJWT } from 'jose';
import { AUTH_API_BASE_URL, API_ENDPOINTS } from '@/config';

const AGENT_AUTH_KEYS = [
    'auth_token', 'accessTokenAgent', 'refreshTokenAgent',
    'headerSecretKeyAgent', 'bodySecretKeyAgent',
    'agent_id', 'agentId', 'tokenDateAgent', 'tokenExpAgent',
    'user', 'otp_session', 'otp_unique_id', 'otp_reference_id', 'otp_mobile',
    'selected_role_id', 'selected_role_type',
];

/**
 * Clear all agent auth data and force a full page reload to the login screen.
 * The hard reload (window.location) guarantees no stale in-memory state survives.
 */
export const logoutAndReload = (): void => {
    AGENT_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
    window.location.href = '/agent-login';
};

// Single in-flight guard so concurrent 401s don't stampede /refresh_token
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the stored refresh token for a new access/refresh pair.
 * On success, overwrites both tokens + tokenExpAgent and resolves true.
 * On any failure, resolves false (caller decides whether to log out).
 */
export const refreshAgentToken = async (): Promise<boolean> => {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const refreshToken = localStorage.getItem('refreshTokenAgent');
        if (!refreshToken) return false;

        try {
            const res = await fetch(`${AUTH_API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!res.ok) return false;
            const data = await res.json();
            if (data.status !== 'success' || !data.access_token) return false;

            localStorage.setItem('accessTokenAgent', data.access_token);
            // Refresh tokens rotate — always persist the new one
            localStorage.setItem('refreshTokenAgent', data.refresh_token);
            const expiresIn = data.expires_in ?? (12 * 60 * 60);
            localStorage.setItem('tokenExpAgent', String(Math.floor(Date.now() / 1000) + expiresIn));
            return true;
        } catch {
            return false;
        }
    })();

    try {
        return await refreshInFlight;
    } finally {
        refreshInFlight = null;
    }
};

/**
 * Build the Authorization header, auto-detecting the token scheme:
 *
 * - Supabase (Mobile & OTP login): accessTokenAgent is an ES256 JWT (3
 *   dot-separated parts). The backend authorizer verifies it directly, so we
 *   send it BARE.
 * - Legacy (Username & Password login): accessTokenAgent is a raw opaque UUID
 *   (no dots). The legacy authorizer expects an HS256 JWT wrapping
 *   `{ token, unique_id, exp }` signed with headerSecretKeyAgent — so we
 *   re-sign it here.
 *
 * @param isAgent - Whether this is an agent request (default: true)
 * @returns Promise that resolves to the Authorization value (bare token or
 *          signed JWT), or empty string.
 */
export const getAuthorizationHeader = async (isAgent: boolean = true): Promise<string> => {
    const prefix = isAgent ? 'Agent' : '';
    const accessToken =
        localStorage.getItem(`accessToken${prefix}`) ||
        localStorage.getItem('auth_token') ||
        '';
    if (!accessToken) return '';

    // A JWT has three dot-separated parts; a legacy UUID has none.
    const isJwt = accessToken.split('.').length === 3;
    if (isJwt) {
        // Supabase token — send as-is.
        return accessToken;
    }

    // Legacy opaque token — re-sign an HS256 JWT with headerSecretKey.
    const idKey = isAgent ? 'agentId' : 'userId';
    const uniqueId =
        localStorage.getItem(idKey) ||
        localStorage.getItem('agent_id') ||
        localStorage.getItem('agentId') ||
        '';
    const headerSecretKey = localStorage.getItem(`headerSecretKey${prefix}`) || '';
    if (!headerSecretKey) {
        // No secret to sign with — fall back to the raw token (will likely 401).
        return accessToken;
    }

    const payload: Record<string, unknown> = {
        token: accessToken,
        unique_id: uniqueId,
    };
    const tokenExpStr = localStorage.getItem(`tokenExp${prefix}`);
    if (tokenExpStr) payload.exp = parseInt(tokenExpStr, 10);

    try {
        return await new SignJWT(payload)
            .setProtectedHeader({ alg: 'HS256' })
            .sign(new TextEncoder().encode(headerSecretKey));
    } catch {
        return accessToken;
    }
};

